import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Upload, ArrowLeft, FileSpreadsheet, Trash2, Plus, Eye, Table, Database, Loader2, Shield, AlertTriangle, CheckCircle } from 'lucide-react';
import { Badge } from '../../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ISYOskaAmp({ ihaleler, onNavigate }) {
  const [selectedIhale, setSelectedIhale] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analiz, setAnaliz] = useState(null);
  const [analizGecmisi, setAnalizGecmisi] = useState([]);
  const [manualDialog, setManualDialog] = useState(false);
  const [manualForm, setManualForm] = useState({ baslik: '', aciklama: '', veri_tipi: 'oska' });
  const [viewItem, setViewItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const fetchOska = useCallback(async (ihaleId) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/isy/oska/${ihaleId}`, { withCredentials: true });
      setItems(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (selectedIhale) { fetchOska(selectedIhale.id); fetchAnalizGecmisi(selectedIhale.id); } }, [selectedIhale, fetchOska]);

  const fetchAnalizGecmisi = async (ihaleId) => {
    try {
      const res = await axios.get(`${API}/isy/mevzuat-analiz/${ihaleId}`, { withCredentials: true });
      setAnalizGecmisi(res.data || []);
      if (res.data?.length > 0) setAnaliz(res.data[0]);
    } catch (e) { console.error(e); }
  };

  const triggerAnaliz = async (oskaId) => {
    setAnalyzing(true);
    setAnaliz(null);
    try {
      const res = await axios.post(`${API}/isy/mevzuat-analiz/${selectedIhale.id}`, { oska_id: oskaId, kaynak: 'oska' }, { withCredentials: true });
      setAnaliz(res.data);
      toast.success('Mevzuat analizi tamamlandı');
      fetchAnalizGecmisi(selectedIhale.id);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Analiz hatası';
      if (detail.includes('API anahtarı')) {
        toast.error('API anahtarınız tanımlı değil. Profil › Yapay Zekâ bölümünden kendi anahtarınızı girin.');
      } else {
        toast.error(detail);
      }
    }
    finally { setAnalyzing(false); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/isy/oska/${selectedIhale.id}/upload`, formData, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${res.data.satir_sayisi || 0} satır veri yüklendi`);
      fetchOska(selectedIhale.id);
      // Otomatik mevzuat analizi tetikle
      if (res.data?.id) {
        triggerAnaliz(res.data.id);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Yükleme hatası';
      if (detail.includes('Dosya okuma') || detail.includes('format')) {
        toast.error('Tanımsız format, lütfen OSKA/AMP standart çıktısı yükleyin.');
      } else {
        toast.error(detail);
      }
    }
    finally { setUploading(false); e.target.value = ''; }
  };

  const handleManualSave = async () => {
    if (!manualForm.baslik.trim()) { toast.error('Başlık zorunlu'); return; }
    try {
      await axios.post(`${API}/isy/oska/${selectedIhale.id}`, {
        ihale_id: selectedIhale.id, ...manualForm
      }, { withCredentials: true });
      toast.success('Veri eklendi');
      setManualDialog(false);
      setManualForm({ baslik: '', aciklama: '', veri_tipi: 'oska' });
      fetchOska(selectedIhale.id);
    } catch (e) { toast.error('Hata oluştu'); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/isy/oska/item/${deleteId}`, { withCredentials: true });
      toast.success('Silindi'); setDeleteId(null); fetchOska(selectedIhale.id);
    } catch (e) { toast.error('Silme hatası'); }
  };

  if (!selectedIhale) {
    const SUB_PAGES = [
      { id: 'idari_sartname', label: 'İdari Şartname / Sözleşme', desc: 'İdari şartname ve sözleşme tasarısı belgeleri — her işe özel temel dokümanlar', color: 'text-cyan-400', bg: 'hover:border-cyan-500/40' },
      { id: 'birim_fiyat', label: 'Birim Fiyat / Pursantaj', desc: 'Birim fiyat teklif cetveli ve pursantaj tabloları', color: 'text-orange-400', bg: 'hover:border-orange-500/40' },
      { id: 'mukayeseli_kesif', label: 'Mukayeseli Keşif', desc: 'İhale yaklaşık maliyet ve keşif karşılaştırma verileri', color: 'text-violet-400', bg: 'hover:border-violet-500/40' },
      { id: 'hakedis', label: 'Hakediş', desc: 'Hakediş raporları — sözleşme, idari şartname, YİGŞ, 4734/4735 ve Kamu İhale Genel Tebliği esas alınır', color: 'text-emerald-400', bg: 'hover:border-emerald-500/40' },
    ];
    return (
      <div className="space-y-5" data-testid="isy-oska-amp">
        <div className="flex items-center gap-2">
          <Upload className="w-5 h-5 text-violet-400" />
          <h2 className="text-lg font-bold text-white">OSKA/AMP Veri Entegrasyonu</h2>
        </div>
        <p className="text-sm text-gray-400">Veri yüklemek veya incelemek istediğiniz alt sayfayı seçin:</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SUB_PAGES.map(sp => (
            <Card key={sp.id} className={`bg-gray-900/60 border-gray-700/50 ${sp.bg} cursor-pointer transition-all hover:scale-[1.01]`} onClick={() => onNavigate?.(sp.id)} data-testid={`oska-sub-${sp.id}`}>
              <CardContent className="py-5 px-5">
                <p className={`text-base font-semibold ${sp.color}`}>{sp.label}</p>
                <p className="text-xs text-gray-500 mt-1">{sp.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* OSKA/AMP Doğrudan Veri Yükleme */}
        <div className="border-t border-gray-800 pt-5">
          <h3 className="text-sm font-semibold text-white mb-3">Doğrudan OSKA/AMP Veri Yükleme</h3>
          <p className="text-xs text-gray-500 mb-3">Bir ihale seçerek Excel/CSV dosyası yükleyin ve mevzuat analizi başlatın:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {(ihaleler || []).map(ih => (
              <Card key={ih.id} className="bg-gray-900/60 border-gray-700/50 hover:border-violet-500/40 cursor-pointer transition-colors" onClick={() => setSelectedIhale(ih)} data-testid={`oska-select-${ih.id}`}>
                <CardContent className="py-3 px-4">
                  <p className="text-sm font-semibold text-white">{ih.is_adi}</p>
                  <p className="text-xs text-gray-500 mt-1">IKN: {ih.ikn || '-'}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="isy-oska-amp">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setSelectedIhale(null)} className="text-gray-400 hover:text-white h-8 px-2">
            <ArrowLeft className="w-4 h-4 mr-1" /> Geri
          </Button>
          <div>
            <h2 className="text-sm font-bold text-white">{selectedIhale.is_adi}</h2>
            <p className="text-xs text-gray-500">OSKA/AMP Veri Yönetimi</p>
          </div>
        </div>
        <div className="flex gap-2">
          <label className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs cursor-pointer transition-colors ${uploading ? 'bg-gray-700 text-gray-500' : 'bg-violet-600 hover:bg-violet-700 text-white'}`}>
            <Upload className="w-3.5 h-3.5" /> {uploading ? 'Yükleniyor...' : 'Excel/CSV Yükle'}
            <input type="file" accept=".xlsx,.xls,.csv" onChange={handleUpload} disabled={uploading} className="hidden" data-testid="oska-file-input" />
          </label>
          <Button size="sm" variant="outline" className="border-gray-600 text-gray-300 hover:bg-gray-800 text-xs" onClick={() => setManualDialog(true)} data-testid="oska-manual-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Manuel Ekle
          </Button>
        </div>
      </div>

      {/* İstatistik */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="pt-4 pb-3 text-center">
            <Database className="w-5 h-5 text-violet-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{items.length}</p>
            <p className="text-xs text-gray-500">Toplam Kayıt</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="pt-4 pb-3 text-center">
            <FileSpreadsheet className="w-5 h-5 text-blue-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-blue-400">{items.filter(i => i.veri_tipi === 'dosya_yukleme').length}</p>
            <p className="text-xs text-gray-500">Dosya Yükleme</p>
          </CardContent>
        </Card>
        <Card className="bg-gray-900/80 border-gray-700/50">
          <CardContent className="pt-4 pb-3 text-center">
            <Table className="w-5 h-5 text-green-400 mx-auto mb-1" />
            <p className="text-lg font-bold text-green-400">{items.reduce((sum, i) => sum + (i.satir_sayisi || 0), 0)}</p>
            <p className="text-xs text-gray-500">Toplam Satır</p>
          </CardContent>
        </Card>
      </div>

      {/* Veri Listesi */}
      {loading ? (
        <div className="text-center text-gray-400 py-8">Yükleniyor...</div>
      ) : items.length === 0 ? (
        <Card className="bg-gray-900/60 border-gray-700/50">
          <CardContent className="py-12 text-center">
            <Upload className="w-8 h-8 text-gray-600 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">Henüz veri yüklenmemiş</p>
            <p className="text-gray-600 text-xs mt-1">Excel (.xlsx) veya CSV dosyası yükleyin</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <Card key={item.id} className="bg-gray-900/60 border-gray-700/50 hover:border-gray-600/50 transition-colors" data-testid={`oska-item-${item.id}`}>
              <CardContent className="py-2.5 px-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <FileSpreadsheet className={`w-5 h-5 flex-shrink-0 ${item.veri_tipi === 'dosya_yukleme' ? 'text-blue-400' : 'text-violet-400'}`} />
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{item.baslik}</p>
                      <div className="flex items-center gap-2 text-[10px] text-gray-500">
                        <span>{item.veri_tipi === 'dosya_yukleme' ? 'Dosya' : item.veri_tipi?.toUpperCase()}</span>
                        {item.satir_sayisi > 0 && <span>{item.satir_sayisi} satır</span>}
                        <span>{item.created_at?.split('T')[0]}</span>
                      </div>
                      {item.aciklama && <p className="text-[10px] text-gray-500">{item.aciklama}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {item.degerler?.rows && (
                      <>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-blue-400" onClick={() => setViewItem(item)}>
                          <Eye className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-gray-400 hover:text-emerald-400" onClick={() => triggerAnaliz(item.id)} disabled={analyzing} title="Mevzuat Analizi Başlat">
                          <Shield className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-red-400" onClick={() => setDeleteId(item.id)}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Mevzuat Analiz Durumu */}
      {(analyzing || analiz) && (
        <Card className={`border ${analyzing ? 'bg-blue-900/15 border-blue-700/50 animate-pulse' : analiz?.analiz?.risk_level === 'kritik' || analiz?.analiz?.risk_level === 'yuksek' ? 'bg-red-900/15 border-red-700/50' : analiz?.analiz?.risk_level === 'orta' ? 'bg-amber-900/15 border-amber-700/50' : 'bg-green-900/15 border-green-700/50'}`}>
          <CardContent className="py-4">
            {analyzing ? (
              <div className="flex items-center gap-3 justify-center">
                <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
                <div>
                  <p className="text-sm font-semibold text-blue-300">Mevzuat Analizi Yapılıyor...</p>
                  <p className="text-xs text-gray-500">YİGŞ Md.21/22, 4735 Md.15 kapsamında denetleniyor</p>
                </div>
              </div>
            ) : analiz?.analiz ? (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className={`w-5 h-5 ${analiz.analiz.risk_level === 'kritik' || analiz.analiz.risk_level === 'yuksek' ? 'text-red-400' : analiz.analiz.risk_level === 'orta' ? 'text-amber-400' : 'text-green-400'}`} />
                    <span className="text-sm font-bold text-white">Mevzuat Denetim Sonucu</span>
                  </div>
                  <Badge className={`text-xs ${analiz.analiz.risk_level === 'kritik' ? 'bg-red-700' : analiz.analiz.risk_level === 'yuksek' ? 'bg-red-600' : analiz.analiz.risk_level === 'orta' ? 'bg-amber-600' : 'bg-green-700'} text-white`}>
                    {analiz.analiz.risk_level === 'kritik' ? 'KRİTİK RİSK' : analiz.analiz.risk_level === 'yuksek' ? 'YÜKSEK RİSK' : analiz.analiz.risk_level === 'orta' ? 'ORTA RİSK' : analiz.analiz.risk_level === 'dusuk' ? 'DÜŞÜK RİSK' : 'BELİRSİZ'}
                  </Badge>
                </div>
                {analiz.analiz.ozet && (
                  <p className="text-xs text-gray-300 bg-gray-800/50 p-2 rounded">{analiz.analiz.ozet}</p>
                )}
                {analiz.analiz.sinir_asimi && (
                  <div className="p-2 bg-red-900/30 border border-red-700/50 rounded flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400" />
                    <span className="text-xs text-red-300 font-medium">Yasal iş artışı sınırı aşılmış! (%{analiz.analiz.is_artisi_orani?.toFixed(1)} / %{analiz.analiz.is_artisi_sinir})</span>
                  </div>
                )}
                {(analiz.analiz.warnings || []).length > 0 && (
                  <div className="space-y-1.5 max-h-48 overflow-y-auto">
                    {analiz.analiz.warnings.map((w, i) => (
                      <div key={i} className={`p-2 rounded text-xs flex gap-2 ${w.severity === 'kritik' || w.severity === 'yuksek' ? 'bg-red-900/20 border border-red-800/30' : w.severity === 'orta' ? 'bg-amber-900/20 border border-amber-800/30' : 'bg-gray-800/50 border border-gray-700/30'}`}>
                        <AlertTriangle className={`w-3.5 h-3.5 flex-shrink-0 mt-0.5 ${w.severity === 'kritik' || w.severity === 'yuksek' ? 'text-red-400' : w.severity === 'orta' ? 'text-amber-400' : 'text-gray-400'}`} />
                        <div>
                          <p className="text-gray-200">{w.message}</p>
                          <div className="flex gap-2 mt-0.5 text-gray-500">
                            {w.madde_ref && <span className="text-blue-400">{w.madde_ref}</span>}
                            {w.kalem_adi && <span>{w.kalem_adi}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-3 text-[10px] text-gray-600">
                  <span>Analiz: {analiz.created_at ? new Date(analiz.created_at).toLocaleString('tr-TR') : ''}</span>
                  <span>Dosya: {analiz.dosya_adi}</span>
                  <span>{analiz.warning_count || 0} uyarı</span>
                </div>
              </div>
            ) : null}
          </CardContent>
        </Card>
      )}

      {/* View Data Dialog */}
      <Dialog open={!!viewItem} onOpenChange={() => setViewItem(null)}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">{viewItem?.baslik}</DialogTitle>
          </DialogHeader>
          {viewItem?.degerler?.rows && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-700">
                    {(viewItem.degerler.headers || []).map((h, i) => (
                      <th key={`header-${h}`} className="text-left py-1.5 px-2 text-gray-400 font-medium">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {viewItem.degerler.rows.slice(0, 50).map((row, i) => (
                    <tr key={`row-${i}`} className="border-b border-gray-800/50 hover:bg-gray-800/20">
                      {(viewItem.degerler.headers || []).map((h, j) => (
                        <td key={`cell-${h}-${j}`} className="py-1 px-2 text-gray-300">{row[h] || '-'}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {viewItem.degerler.rows.length > 50 && (
                <p className="text-xs text-gray-500 text-center py-2">+{viewItem.degerler.rows.length - 50} satır daha</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Manual Add Dialog */}
      <Dialog open={manualDialog} onOpenChange={setManualDialog}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Manuel Veri Ekle</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-gray-400 text-xs">Başlık *</Label>
              <Input value={manualForm.baslik} onChange={e => setManualForm({...manualForm, baslik: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" data-testid="oska-form-baslik" />
            </div>
            <div>
              <Label className="text-gray-400 text-xs">Açıklama</Label>
              <Input value={manualForm.aciklama} onChange={e => setManualForm({...manualForm, aciklama: e.target.value})} className="bg-gray-800 border-gray-700 text-white h-8 text-sm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setManualDialog(false)} className="text-gray-400">İptal</Button>
            <Button onClick={handleManualSave} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="oska-form-save">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-gray-900 border-gray-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Veriyi Sil</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">Bu veri kaydını silmek istediğinize emin misiniz?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-800 text-gray-300 border-gray-700">İptal</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
